/**
 * 05 사용자 API — Partner Users.
 * 거래처 정보의 상위업체 = 로그인 업체인 사용자(업체) 목록. 조회 전용.
 */
import { createApiPath } from '@/shared/api/endpoints';
import { apiClient, toQueryParams } from '@/shared/api/http-client';
import type { PageParams, PageResponse } from '@/shared/api/types';

/**
 * 정렬 기준 (ruda-api #2209). 동률은 모두 업체명 → 사용자 ID 오름차순. 정의되지 않은 값은 400.
 * - COMPANY_NAME(기본): 업체명 오름차순
 * - REGISTRATION_COUNT_DESC: 직전 월 이미지 수 내림차순 — 거래 없음(null)은 0건과 함께 취급
 * - REGISTRATION_COUNT_ASC: 직전 월 이미지 수 오름차순 — 거래 없음(null)은 맨 뒤
 * - TRADING_CLINIC_COUNT_DESC: 거래 병의원 수 내림차순
 * 많은순·적은순은 서로 뒤집힌 목록이 아니므로 바꿀 때마다 API를 다시 부른다.
 */
export const PARTNER_USER_ORDERS = [
  'COMPANY_NAME',
  'REGISTRATION_COUNT_DESC',
  'REGISTRATION_COUNT_ASC',
  'TRADING_CLINIC_COUNT_DESC',
] as const;

export type PartnerUserOrder = (typeof PARTNER_USER_ORDERS)[number];

export interface PartnerUserListParams extends PageParams {
  /** 업체명·대표자명·이메일 부분 검색 */
  keyword?: string;
  order?: PartnerUserOrder;
}

/** 월별 처방 등록 이미지 수 */
export interface MonthlyRegistrationCount {
  /** yyyyMM */
  registrationYearMonth: string;
  /** 그 달 거래 병의원이 없으면 null (화면 "-"), 있으면 0 이상 */
  imageCount: number | null;
}

export interface PartnerUser {
  userId: number;
  companyName: string;
  representativeName: string;
  /** 마스킹 없이 전체 노출 */
  email: string;
  phoneNumber: string;
  /** 현재 거래 중인 병의원 수 (병의원 중복 제거) */
  tradingClinicCount: number;
  /** 기준 월(이번 달) 포함 최근 3개월, 오래된 월부터. 화면 라벨은 registrationYearMonth 를 그대로 쓴다 */
  monthlyRegistrations: MonthlyRegistrationCount[];
}

export const userService = {
  /** GET /partner/users */
  list: async (params: PartnerUserListParams = {}) => {
    const { data } = await apiClient.get<PageResponse<PartnerUser>>(
      createApiPath.users.list(),
      { params: toQueryParams(params) },
    );
    return data;
  },
};
